// Libs
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';
import { MinusOutlined, PlusOutlined } from '@ant-design/icons';
import { debounce } from 'utils/libs';
// Actions
import { sendToast } from 'screens/Dashboard/actions';
// Components
import {
	Button,
	InputNumber,
	LogoIcon,
	Span,
	Tooltip,
	Wrapper,
} from 'components';
import useOrderApi from 'core/hooks/useCoreApi';
import {
	getCurrentTransactionAmount,
	getEditableTransactionAmountPatch,
	getEditableTransactionLimitAmount,
	getFormattedReadOnlyTransactionValue,
	formatTransactionQuantityInput,
	parseTransactionQuantityInput,
} from './TransactionValue.utils';
import {
	OPTIMISTIC_SCAN_ERROR_FIELD,
	OPTIMISTIC_SCAN_STATUS,
	OPTIMISTIC_SCAN_STATUS_FIELD,
	getOptimisticScanError,
	isOptimisticScanError,
	isOptimisticScanPending,
} from 'core/ElementViewBuilder/views/TransactionViewer/scanStatus';

const TransactionValueContainer = styled.div`
	display: flex;
	align-items: center;
	justify-content: ${({ $centerContent }) =>
		$centerContent ? 'center' : 'flex-start'};
	width: 100%;
	height: ${({ $compact }) => ($compact ? 'auto' : '100%')};
`;

const CenteredInputNumber = styled(InputNumber)`
	${({ $centerContent }) =>
		$centerContent &&
		`\n\t\t& .ant-input-number-input {\n\t\t\ttext-align: center;\n\t\t}\n\t`}
`;

const AmountInputContainer = styled.div`
	position: relative;
	width: 100%;
`;

const AmountSavingIndicator = styled.div`
	position: absolute;
	top: 50%;
	right: ${({ $compact }) => ($compact ? '44px' : '4px')};
	width: 20px;
	height: 20px;
	display: flex;
	align-items: center;
	justify-content: center;
	pointer-events: none;
	transform: translateY(-50%) scale(0.82);
	transform-origin: center;
	opacity: 0.85;
`;

const CompactAmountControl = styled.div`
	display: grid;
	grid-template-columns: 40px minmax(0, 1fr) 40px;
	align-items: stretch;
	width: 100%;
	min-height: 36px;

	.ant-input-number {
		border-radius: 0;
	}
`;

const AmountStepButton = styled.button`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 40px;
	min-width: 40px;
	height: 100%;
	min-height: 36px;
	padding: 0;
	border: 1px solid rgba(127, 127, 127, 0.52);
	border-radius: ${({ $side }) =>
		$side === 'left' ? '7px 0 0 7px' : '0 7px 7px 0'};
	background: rgba(127, 127, 127, 0.08);
	color: inherit;

	&:hover:not(:disabled),
	&:focus-visible {
		border-color: #1eb980;
		color: #1eb980;
	}

	&:disabled {
		cursor: not-allowed;
		opacity: 0.42;
	}
`;

const OptimisticErrorActions = styled.div`
	display: inline-flex;
	align-items: center;
	gap: 5px;
`;

const AmountValue = ({
	rowData,
	stateTable = {},
	updateArticleInTransit,
	centerContent,
	field = {},
	onOptimisticAmountChange,
}) => {
	const { isTrasactionOpen, updateItemOnTable, setItemMutationPending } =
		stateTable;
	const mutationKey = `amount:${stateTable.wtdId}:${
		rowData.id || rowData.item_id
	}`;
	const setMutationPending = isPending =>
		setItemMutationPending?.(mutationKey, isPending);

	const debounced = React.useCallback(
		debounce(callback => callback(), 2000),
		[],
	);
	const lastRawInputRef = React.useRef();
	const isAmountValueMountedRef = React.useRef(true);

	const [state, _setState] = useState({
		isEditable: isTrasactionOpen,
		amount: getCurrentTransactionAmount(rowData, {
			usesAmountAsCurrentValue: field.usesAmountAsCurrentValue,
		}),
		inputValue: undefined,
		isFetching: false,
	});

	const setState = newState =>
		_setState(currentState => ({ ...currentState, ...newState }));

	useEffect(
		() => () => {
			isAmountValueMountedRef.current = false;
			// A virtualized row may unmount without leaving the document. Flush the
			// user's already-registered intent instead of leaving optimistic UI that
			// was never persisted.
			debounced.flush?.();
		},
		[debounced],
	);

	const setValue = (value, { keepRawInput = false } = {}) => {
		const limitAmount = getEditableTransactionLimitAmount(rowData, {
			usesAmountAsCurrentValue: field.usesAmountAsCurrentValue,
		});
		const numericValue = Number(value || 0);
		const isInvalidNumber = Number.isNaN(numericValue);

		if (isInvalidNumber) {
			setState({ inputValue: String(value ?? '') });
			return;
		}

		const isOverAvailable = numericValue > limitAmount;

		const amount = isOverAvailable ? limitAmount : numericValue;
		const optimisticAmountPatch = getEditableTransactionAmountPatch(amount, {
			usesAmountAsCurrentValue: field.usesAmountAsCurrentValue,
		});
		// Preserve exactly what the user is typing while the field has focus. The
		// canonical amount is still clipped before it reaches the table or API and
		// becomes visible when the input loses focus.
		const inputValue = keepRawInput ? String(value ?? '') : undefined;

		setState({ amount, inputValue });
		updateItemOnTable?.(
			{ id: rowData.id, itemId: rowData.item_id },
			optimisticAmountPatch,
		);
		onOptimisticAmountChange?.(optimisticAmountPatch);
		setMutationPending(true);

		debounced(() => {
			if (isAmountValueMountedRef.current) setState({ isFetching: true });
			const finishMutation = () => {
				if (isAmountValueMountedRef.current) {
					setState({ isFetching: false });
					setMutationPending(false);
				}
			};

			updateArticleInTransit({
				isDeleteArticle: amount == 0,
				fields: { amount },
				onThen: finishMutation,
				onCatch: finishMutation,
			});
		});
	};

	const handleInput = value => {
		const parsedValue = parseTransactionQuantityInput(value);

		lastRawInputRef.current = parsedValue;
		setValue(parsedValue, { keepRawInput: true });
	};

	const handleChange = value => {
		const parsedValue = parseTransactionQuantityInput(value);

		if (lastRawInputRef.current === parsedValue) {
			lastRawInputRef.current = undefined;
			return;
		}

		setValue(parsedValue);
	};

	const stepValue = direction => {
		const currentAmount = Number(
			parseTransactionQuantityInput(state.inputValue ?? state.amount) || 0,
		);
		const limitAmount = getEditableTransactionLimitAmount(rowData, {
			usesAmountAsCurrentValue: field.usesAmountAsCurrentValue,
		});
		const nextAmount = Math.min(
			limitAmount,
			Math.max(0, currentAmount + direction),
		);

		if (nextAmount === currentAmount) return;

		lastRawInputRef.current = undefined;
		setValue(nextAmount);
	};

	useEffect(() => {
		if (state.inputValue !== undefined) return;

		setState({
			amount: getCurrentTransactionAmount(rowData, {
				usesAmountAsCurrentValue: field.usesAmountAsCurrentValue,
			}),
		});
	}, [
		rowData.amount,
		rowData.wtdi_amount,
		field.usesAmountAsCurrentValue,
		state.inputValue,
	]);

	return (
		<Wrapper
			justifyContent={centerContent ? 'center' : undefined}
			alignItems={centerContent ? 'center' : undefined}
		>
			{!!state.isEditable ? (
				<AmountInputContainer>
					{field.compactTransactionCard ? (
						<CompactAmountControl>
							<AmountStepButton
								$side='left'
								type='button'
								aria-label='Disminuir cantidad'
								disabled={Number((state.inputValue ?? state.amount) || 0) <= 0}
								onClick={() => stepValue(-1)}
							>
								<MinusOutlined aria-hidden='true' />
							</AmountStepButton>
							<CenteredInputNumber
								$centerContent={centerContent}
								controls={false}
								placeholder='Cantidad'
								value={(state.inputValue ?? state.amount) || undefined}
								width='100%'
								formatter={(value, info) =>
									state.inputValue !== undefined
										? state.inputValue
										: formatTransactionQuantityInput(value, info)
								}
								parser={parseTransactionQuantityInput}
								onInput={handleInput}
								onChange={handleChange}
								onBlur={() => setState({ inputValue: undefined })}
							/>
							<AmountStepButton
								$side='right'
								type='button'
								aria-label='Aumentar cantidad'
								disabled={
									Number((state.inputValue ?? state.amount) || 0) >=
									getEditableTransactionLimitAmount(rowData, {
										usesAmountAsCurrentValue: field.usesAmountAsCurrentValue,
									})
								}
								onClick={() => stepValue(1)}
							>
								<PlusOutlined aria-hidden='true' />
							</AmountStepButton>
						</CompactAmountControl>
					) : (
						<CenteredInputNumber
							$centerContent={centerContent}
							placeholder='Cantidad'
							value={(state.inputValue ?? state.amount) || undefined}
							width='100%'
							formatter={(value, info) =>
								state.inputValue !== undefined
									? state.inputValue
									: formatTransactionQuantityInput(value, info)
							}
							parser={parseTransactionQuantityInput}
							onInput={handleInput}
							onChange={handleChange}
							onBlur={() => setState({ inputValue: undefined })}
						/>
					)}
					{state.isFetching && (
						<AmountSavingIndicator
							$compact={field.compactTransactionCard}
							aria-label='Guardando cantidad'
						>
							<LogoIcon spin />
						</AmountSavingIndicator>
					)}
				</AmountInputContainer>
			) : (
				<Span maxHeight='200px' overflow='auto'>
					{state.amount}
				</Span>
			)}
		</Wrapper>
	);
};

const ButtonType = ({
	rowData,
	stateTable = {},
	updateArticleInTransit,
	centerContent,
}) => {
	const {
		setItemMutationPending,
		retryOptimisticScan,
		removeFailedOptimisticScan,
	} = stateTable;
	const mutationKey = `serie:${rowData.id || rowData.item_id}:${rowData.serie}`;
	const setMutationPending = isPending =>
		setItemMutationPending?.(mutationKey, isPending);
	const [isFetching, setIsFetching] = useState(false);
	const isAdded = Boolean(rowData.wtdi_id ?? rowData.wtdiId);
	const titleButton = isAdded ? 'Quitar' : 'Agregar';
	const finishMutation = () => {
		setIsFetching(false);
		setMutationPending(false);
	};

	const onDeleteSerie = () => {
		updateArticleInTransit({
			isDeleteArticle: true,
			onThen: finishMutation,
			onCatch: finishMutation,
		});
	};

	const onAddSerie = () => {
		updateArticleInTransit({
			fields: { serieId: rowData.serie },
			onThen: finishMutation,
			onCatch: finishMutation,
		});
	};

	const retryOptimisticSerie = () => {
		if (!retryOptimisticScan) return;

		retryOptimisticScan({
			...rowData,
			[OPTIMISTIC_SCAN_STATUS_FIELD]: OPTIMISTIC_SCAN_STATUS.PENDING,
			[OPTIMISTIC_SCAN_ERROR_FIELD]: undefined,
		});
	};

	if (isOptimisticScanPending(rowData) || isFetching) return <LogoIcon spin />;

	if (isOptimisticScanError(rowData)) {
		const persistedWtdiId = rowData.wtdi_id ?? rowData.wtdiId;
		const removeOptimisticSerie = () => {
			if (removeFailedOptimisticScan) {
				removeFailedOptimisticScan(rowData);
				return;
			}

			setMutationPending(true);
			setIsFetching(true);
			onDeleteSerie();
		};

		return (
			<OptimisticErrorActions>
				<Tooltip title={getOptimisticScanError(rowData)}>
					<Button
						className='desktop-transactions-button-add-delete-serie'
						style={{
							color: '#f44336',
							width: centerContent ? 'auto' : undefined,
						}}
						onClick={retryOptimisticSerie}
					>
						Error
					</Button>
				</Tooltip>
				<span>/</span>
				<Button
					className='desktop-transactions-button-add-delete-serie'
					style={{ width: centerContent ? 'auto' : undefined }}
					disabled={!persistedWtdiId && !removeFailedOptimisticScan}
					onClick={removeOptimisticSerie}
				>
					Quitar
				</Button>
			</OptimisticErrorActions>
		);
	}

	return (
		<Button
			className='desktop-transactions-button-add-delete-serie'
			style={{
				color: !isAdded && 'darkgray',
				width: centerContent ? 'auto' : undefined,
			}}
			onClick={() => {
				setMutationPending(true);
				setIsFetching(true);
				isAdded ? onDeleteSerie() : onAddSerie();
			}}
		>
			{titleButton}
		</Button>
	);
};

const CustomConponent = {
	input: AmountValue,
	button: ButtonType,
};

const isSerializedTransactionValue = rowData => {
	const serializedValue = rowData?.is_serialized ?? rowData?.isSerialized;

	return (
		serializedValue === true ||
		['1', 'true', 't', 'si', 'sí'].includes(
			String(serializedValue || '').toLowerCase(),
		)
	);
};

const TransactionValue = ({ rowData, stateTable, field = {} }) => {
	const isSerialized = isSerializedTransactionValue(rowData);
	const typeComponent = isSerialized ? 'button' : 'input';
	const isMountedRef = React.useRef(true);
	const amountMutationRef = React.useRef();
	if (typeComponent === 'input' && !amountMutationRef.current) {
		const registryKey = `${stateTable.wtdId}:${rowData.id || rowData.item_id}`;
		const existingCoordinator =
			stateTable.amountMutationRegistry?.get(registryKey);
		const coordinator = existingCoordinator || {
			documentId: stateTable.wtdId,
			inFlight: false,
			queued: null,
			callbacks: null,
			invalidated: false,
			persistedWtdiIdRef: { current: rowData.wtdi_id },
		};
		stateTable.amountMutationRegistry?.set(registryKey, coordinator);
		amountMutationRef.current = coordinator;
	}
	if (typeComponent === 'input' && amountMutationRef.current)
		amountMutationRef.current.activeCellRef = isMountedRef;
	const persistedWtdiIdRef = amountMutationRef.current?.persistedWtdiIdRef;
	const [state, setState] = useState({
		wtdiId: rowData.wtdi_id,
		wtdiAmount: rowData.wtdi_amount,
	});

	const { isTrasactionOpen, isTransactionFrozen, isOriginTransaction } =
		stateTable;
	const { getData, wtdId, updateItemOnTable, setItemMutationPending } =
		stateTable;
	const { upsertArticle, deleteArticlesInTransit } = useOrderApi();
	const dispatch = useDispatch();

	useEffect(
		() => () => {
			isMountedRef.current = false;
		},
		[],
	);

	useEffect(() => {
		if (typeComponent !== 'input') return;

		persistedWtdiIdRef.current = rowData.wtdi_id;

		setState(currentState => ({
			...currentState,
			wtdiId: persistedWtdiIdRef.current,
			wtdiAmount: rowData.wtdi_amount,
		}));
	}, [typeComponent, rowData.wtdi_id, rowData.wtdi_amount]);

	if (!isTrasactionOpen) {
		const displayValue = getFormattedReadOnlyTransactionValue({
			rowData,
			isTransactionFrozen,
			isOriginTransaction,
			usesAmountAsCurrentValue: field.usesAmountAsCurrentValue,
		});
		const content = <Span>{displayValue}</Span>;

		return isOptimisticScanError(rowData) ? (
			<Tooltip title={getOptimisticScanError(rowData)}>{content}</Tooltip>
		) : (
			content
		);
	}

	const ConponentSwitcher = CustomConponent[typeComponent];
	const sendMessage = ({ message, type = 'success' }) => {
		dispatch(
			sendToast({
				message,
				type,
				duration: 2,
			}),
		);
	};
	const areEquivalentAmountMutations = (left, right) =>
		!!left &&
		!!right &&
		left.isDeleteArticle === right.isDeleteArticle &&
		(left.isDeleteArticle || left.fields?.amount === right.fields?.amount);

	const finishAmountMutation = ({ succeeded, error }) => {
		const coordinator = amountMutationRef.current;
		const callbacks = coordinator.callbacks;

		coordinator.inFlight = false;
		coordinator.queued = null;
		coordinator.callbacks = null;
		setItemMutationPending?.(
			`amount:${wtdId}:${rowData.id || rowData.item_id}`,
			false,
		);

		if (coordinator.invalidated || !coordinator.activeCellRef?.current) return;
		succeeded ? callbacks?.onThen?.() : callbacks?.onCatch?.(error);
	};

	const reconcileAmountMutation = (persistedMutation, executeMutation) => {
		const coordinator = amountMutationRef.current;
		if (coordinator.invalidated) {
			finishAmountMutation({
				succeeded: false,
				error: new Error('Transaction invalidated'),
			});
			return;
		}
		const nextMutation = coordinator.queued;
		coordinator.queued = null;

		if (
			nextMutation &&
			!areEquivalentAmountMutations(nextMutation, persistedMutation)
		) {
			executeMutation(nextMutation);
			return;
		}

		// Detail reads intentionally remain latest-only. A refresh superseded by a
		// newer row mutation may be aborted; the newest full-table refresh is the
		// canonical result, so an aborted stale read is not a write failure.
		Promise.resolve(getData({ wtdiId: persistedWtdiIdRef.current })).finally(
			() => {
				const mutationQueuedDuringRefresh = coordinator.queued;
				coordinator.queued = null;

				if (
					mutationQueuedDuringRefresh &&
					!areEquivalentAmountMutations(
						mutationQueuedDuringRefresh,
						persistedMutation,
					)
				) {
					executeMutation(mutationQueuedDuringRefresh);
					return;
				}

				finishAmountMutation({ succeeded: true });
			},
		);
	};

	const executeAmountMutation = mutation => {
		const coordinator = amountMutationRef.current;
		if (coordinator.invalidated) {
			finishAmountMutation({
				succeeded: false,
				error: new Error('Transaction invalidated'),
			});
			return;
		}

		const onMutationError = error => {
			if (coordinator.invalidated) {
				finishAmountMutation({ succeeded: false, error });
				return;
			}

			const latestMutation = coordinator.queued;
			coordinator.queued = null;

			Promise.resolve(getData({ wtdiId: persistedWtdiIdRef.current })).finally(
				() => {
					const mutationQueuedDuringRefresh = coordinator.queued;
					coordinator.queued = null;
					const mutationToRetry = mutationQueuedDuringRefresh || latestMutation;

					if (mutationToRetry && !mutation.isDeleteArticle) {
						executeAmountMutation(mutationToRetry);
						return;
					}

					const customMessage =
						error?.name == 'SequelizeDatabaseError' &&
						'Cantidad disponible exedida';
					sendMessage({
						message: customMessage || error?.message,
						type: 'error',
					});
					finishAmountMutation({ succeeded: false, error });
				},
			);
		};

		if (mutation.isDeleteArticle) {
			const persistedWtdiId = persistedWtdiIdRef.current;
			if (!persistedWtdiId) {
				reconcileAmountMutation(mutation, executeAmountMutation);
				return;
			}

			deleteArticlesInTransit([persistedWtdiId])
				.then(() => {
					if (coordinator.invalidated) {
						finishAmountMutation({
							succeeded: false,
							error: new Error('Transaction invalidated'),
						});
						return;
					}
					persistedWtdiIdRef.current = null;
					if (isMountedRef.current)
						setState(currentState => ({
							...currentState,
							wtdiId: null,
						}));
					updateItemOnTable(
						{ id: rowData.id, itemId: rowData.item_id },
						{ wtdi_id: null },
					);
					reconcileAmountMutation(mutation, executeAmountMutation);
				})
				.catch(onMutationError);
			return;
		}

		upsertArticle({
			ids: {
				wtdiId: persistedWtdiIdRef.current,
				wtdId,
				itemId: rowData.item_id,
			},
			fields: mutation.fields,
		})
			.then(({ wtdiId: responseWtdiId } = {}) => {
				if (coordinator.invalidated) {
					finishAmountMutation({
						succeeded: false,
						error: new Error('Transaction invalidated'),
					});
					return;
				}
				const persistedWtdiId = responseWtdiId || persistedWtdiIdRef.current;
				persistedWtdiIdRef.current = persistedWtdiId;
				if (isMountedRef.current)
					setState(currentState => ({
						...currentState,
						wtdiId: persistedWtdiId,
					}));
				updateItemOnTable(
					{ id: rowData.id, itemId: rowData.item_id, serie: rowData.serie },
					{ wtdi_id: persistedWtdiId },
				);
				reconcileAmountMutation(mutation, executeAmountMutation);
			})
			.catch(onMutationError);
	};

	const queueAmountMutation = mutation => {
		const coordinator = amountMutationRef.current;
		if (coordinator.invalidated) {
			mutation.onCatch?.(new Error('Transaction invalidated'));
			return;
		}
		coordinator.callbacks = {
			onThen: mutation.onThen,
			onCatch: mutation.onCatch,
		};

		if (coordinator.inFlight) {
			// Only the latest desired value matters for this row. Other rows have
			// their own coordinator and remain fully concurrent.
			coordinator.queued = mutation;
			return;
		}

		coordinator.inFlight = true;
		executeAmountMutation(mutation);
	};

	const updateArticleInTransit = ({
		isDeleteArticle = false,
		fields,
		onThen = () => {},
		onCatch = () => {},
	}) => {
		if (typeComponent === 'input') {
			queueAmountMutation({
				isDeleteArticle,
				fields,
				onThen,
				onCatch,
			});
			return;
		}

		const serializedWtdiId = rowData.wtdi_id ?? rowData.wtdiId;

		if (isDeleteArticle && !serializedWtdiId) {
			onThen();
			return;
		}

		isDeleteArticle
			? deleteArticlesInTransit([serializedWtdiId])
					.then(() => Promise.resolve(getData()).then(onThen))
					.catch(res => {
						sendMessage({ message: res.message, type: 'error' });
						return Promise.resolve(getData({ wtdiId: serializedWtdiId })).then(
							onCatch,
						);
					})
			: upsertArticle({
					ids: {
						wtdiId: serializedWtdiId,
						wtdId,
						itemId: rowData.item_id,
					},
					fields: fields,
				})
					.then(({ wtdiId }) => {
						updateItemOnTable(
							{ id: rowData.id, itemId: rowData.item_id, serie: rowData.serie },
							{ wtdi_id: wtdiId },
						);
						return Promise.resolve(
							getData({ wtdiId: wtdiId || serializedWtdiId }),
						).then(onThen);
					})
					.catch(err => {
						const customMessage =
							err.name == 'SequelizeDatabaseError' &&
							'Cantidad disponible exedida';
						sendMessage({ message: customMessage, type: 'error' });
						return Promise.resolve(getData({ wtdiId: serializedWtdiId })).then(
							onCatch,
						);
					});
	};

	const _rowData = {
		...rowData,
		wtdi_id:
			typeComponent === 'input'
				? state.wtdiId
				: (rowData.wtdi_id ?? rowData.wtdiId),
		wtdi_amount:
			typeComponent === 'input' ? state.wtdiAmount : rowData.wtdi_amount,
	};

	const handleOptimisticAmountChange = amountPatch => {
		if (!Object.prototype.hasOwnProperty.call(amountPatch, 'wtdi_amount'))
			return;

		setState(currentState => ({
			...currentState,
			wtdiAmount: amountPatch.wtdi_amount,
		}));
	};

	return (
		<TransactionValueContainer
			$centerContent={field.centerContent}
			$compact={field.compactTransactionCard}
		>
			<ConponentSwitcher
				rowData={_rowData}
				stateTable={stateTable}
				updateArticleInTransit={updateArticleInTransit}
				centerContent={field.centerContent}
				field={field}
				onOptimisticAmountChange={handleOptimisticAmountChange}
			/>
		</TransactionValueContainer>
	);
};

export default TransactionValue;
